/**
 * Tooltip - Core Component (Passthrough Wrapper)
 *
 * This is a thin wrapper around shadcn/ui Tooltip components.
 * It provides a consistent import path and allows for future project-specific
 * defaults or modifications without changing consumer code.
 *
 * Purpose:
 * - Consistent import path: always import from '@/app/_components/core'
 * - Type re-exports for convenience
 * - Default sideOffset of 4 for consistent spacing
 * - Future-proof: modifications can be added here without breaking changes
 *
 * This component does NOT add custom styling - it passes through
 * all props directly to the underlying shadcn/ui components.
 *
 * @see {@link file://./../ui/tooltip.tsx} for the underlying implementation
 */
'use client';

import { forwardRef, type ComponentRef, type ReactNode } from 'react';
import {
  Tooltip as ShadcnTooltip,
  TooltipTrigger as ShadcnTooltipTrigger,
  TooltipContent as ShadcnTooltipContent,
  TooltipProvider as ShadcnTooltipProvider,
} from '@/app/_components/ui/tooltip';
import { cn } from '@/app/_lib/utils';

export interface TooltipProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  delayDuration?: number;
  disableHoverableContent?: boolean;
  children?: ReactNode;
}

export interface TooltipTriggerProps {
  asChild?: boolean;
  children?: ReactNode;
  className?: string;
}

export interface TooltipContentProps {
  className?: string;
  sideOffset?: number;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  children?: ReactNode;
}

export interface TooltipProviderProps {
  delayDuration?: number;
  skipDelayDuration?: number;
  disableHoverableContent?: boolean;
  children: ReactNode;
}

/**
 * Tooltip root component - wrapper over shadcn/ui Tooltip
 *
 * @example
 * ```tsx
 * <Tooltip>
 *   <TooltipTrigger asChild>
 *     <Button variant="outline">Hover</Button>
 *   </TooltipTrigger>
 *   <TooltipContent>
 *     <p>Add to library</p>
 *   </TooltipContent>
 * </Tooltip>
 * ```
 */
export const Tooltip = ({ ...props }: TooltipProps) => {
  return <ShadcnTooltip {...props} />;
};

Tooltip.displayName = 'Tooltip';

/**
 * TooltipProvider - Wraps your app to provide default values for all tooltips
 *
 * @example
 * ```tsx
 * <TooltipProvider delayDuration={200}>
 *   <App />
 * </TooltipProvider>
 * ```
 */
export const TooltipProvider = ({ ...props }: TooltipProviderProps) => {
  return <ShadcnTooltipProvider {...props} />;
};

TooltipProvider.displayName = 'TooltipProvider';

/**
 * TooltipTrigger - Element that triggers the tooltip on hover/focus
 */
export const TooltipTrigger = forwardRef<
  ComponentRef<typeof ShadcnTooltipTrigger>,
  TooltipTriggerProps
>(({ className, ...props }, ref) => {
  return (
    <ShadcnTooltipTrigger ref={ref} className={cn(className)} {...props} />
  );
});

TooltipTrigger.displayName = 'TooltipTrigger';

/**
 * TooltipContent - The content area of the tooltip
 */
export const TooltipContent = forwardRef<
  ComponentRef<typeof ShadcnTooltipContent>,
  TooltipContentProps
>(({ className, sideOffset = 4, ...props }, ref) => {
  return (
    <ShadcnTooltipContent
      ref={ref}
      sideOffset={sideOffset}
      className={cn(className)}
      {...props}
    />
  );
});

TooltipContent.displayName = 'TooltipContent';

export default Tooltip;
